import { DEFAULTS } from "../defaults";
import {
  normalizeAddress,
  normalizeCarrierArr,
  cleanPhone,
  mapDriver,
  mapVehicle,
  mapTrailer,
  sumTrailerValues,
  mapLoss,
  fromYN_bool,
  fromYN_yesno,
  fromYN_yesOnly,
  normalizeCompanyGaragingAddress,
  normalizeCompanyMailingAddress
} from "./helpers";

const isValidEmail = (email = "") =>
  typeof email === "string" &&
  /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim());

/** Parse dates that come as "MM/DD/YYYY" safely */
const parseUsDate = (d) => {
  if (!d || typeof d !== "string") return new Date();
  const [mm, dd, yyyy] = d.split("/").map((s) => Number(s));
  if (!mm || !dd || !yyyy) return new Date(d);
  return new Date(yyyy, mm - 1, dd);
};

const toAddress = (src) =>
  normalizeAddress(
    !src
      ? undefined
      : {
          address1: src.mailingStreet ?? src.garageStreet ?? src.shippingStreet ?? "",
          address2: src.mailingStreet2 ?? src.garageStreet2 ?? src.shippingStreet2 ?? "",
          city: src.mailingCity ?? src.garageCity ?? src.shippingCity ?? "",
          state: src.mailingState ?? src.garageState ?? src.shippingState ?? "",
          zip: src.mailingZip ?? src.garageZip ?? src.shippingZip ?? "",
          county: src.mailingCounty ?? src.garageCounty ?? src.shippingCounty ?? "",
          country: src.mailingCountry ?? src.garageCountry ?? src.shippingCountry ?? "",
        }
  );

const hasAddressValues = (addr = {}) => {
  const values = [
    addr.address1,
    addr.address2,
    addr.city,
    addr.state,
    addr.zip,
  ];

  return values.some((v) => {
    if (v == null) return false;
    if (typeof v === "string") return v.trim().length > 0;
    return true;
  });
};

export function mapQuoteToDefaults(data, producers, company = {}) {
  const quote = data.quote ?? {};
  let producer = producers?.data?.users?.find((p) => p.id === quote.producer);
  producer = producer
    ? { value: String(producer.id), label: producer.name, email: producer.email }
    : null;
  const carrierNormalized = normalizeCarrierArr(
    typeof data.carrier === "string" ? [data.carrier] : data.carrier
  );
  const hasAccelerant = carrierNormalized.includes("Accelerant");

  const cov = quote.coverage ?? {};
  const ops = quote.operations ?? {};
  const insured = quote.insuredInformation ?? {};
  const limits = quote.limits ?? {};
  const radius = quote.radius ?? {};
  const stateMileage = quote.stateMileage ?? [];
  const retailAgent = quote.retailAgent ?? {};
  const step5Raw = quote.step5 ?? {};

  const vehicles = Array.isArray(quote.vehicles) ? quote.vehicles : [];
  const trailers = Array.isArray(quote.trailers) ? quote.trailers : [];
  const drivers = Array.isArray(quote.drivers) ? quote.drivers : [];
  const lossesArray = quote.losses ? Object.values(quote.losses) : [];
  const documents = (quote?.step10?.documents ?? quote?.documents ?? []).map((doc) => ({
    type: doc?.type,
    url: doc?.url,
    name: doc?.name ?? "",
    id: doc?.id ?? doc?._id,
  }));

  const companyMailingAddress = normalizeCompanyMailingAddress(company);
  const companyGaragingAddress = normalizeCompanyGaragingAddress(company);

  const mailingAddressFromQuote = toAddress(quote.mailingAddress);
  const mailingAddress = hasAddressValues(mailingAddressFromQuote)
    ? mailingAddressFromQuote
    : companyMailingAddress;

  // const garagingSameAsMailing = fromYN_bool(quote?.garagingSameAsMailing, false);
  const garagingAddressFromQuote = toAddress(quote.garageAddress);

  const garagingAddress = hasAddressValues(garagingAddressFromQuote)
    ? garagingAddressFromQuote
    : hasAddressValues(companyGaragingAddress)
    ? companyGaragingAddress
    : mailingAddress;

  return {
    ...DEFAULTS,

    // STEP 1
    step1: {
      producer: producer?.value ?? DEFAULTS.step1.producer,
      agentEmail: isValidEmail(data?.agent_email)
        ? data.agent_email
        : isValidEmail(producer?.email)
        ? producer.email
        : isValidEmail(producers?.data?.users?.[0]?.email)
        ? producers.data.users[0].email
        : DEFAULTS.step1.agentEmail,
      policyDate: parseUsDate(cov.effectiveDate),
      carrier: carrierNormalized,

      // DB now holds Y/N
      insuredWithProgressiveCommercialAuto: fromYN_yesno(quote?.step1?.insuredWithProgressiveCommercialAuto, "na"),
      hazmatPlacard: fromYN_bool(quote?.step1?.hazmatPlacard, false),
      haulDoubleTripleTrailers: fromYN_bool(quote?.step1?.haulDoubleTripleTrailers, false),
      operatedTwoYears: fromYN_yesno(quote?.step1?.operatedTwoYears, "na"),
      personalUseOfTrailer: fromYN_bool(quote?.step1?.personalUseOfTrailer, false),

      // Ops.personalUseOfVehicle is Y/N in DB
      personalUseOfVehicle: fromYN_bool(ops.personalUseOfVehicle, false),
      garageState:
        quote?.step1?.garageState ??
        DEFAULTS.step1.garageState,
    },

    // STEP 2
    step2: {
      carrier: carrierNormalized,
      dot: {
        hasUsdot: insured.dotNumber ? "yes" : "no",
        dotNumber: insured.dotNumber ? Number(insured.dotNumber) : undefined,
      },
      motorCarrierNumber: insured.motorCarrierNumber ?? quote?.step2?.motorCarrierNumber ?? undefined,
      hasLossRuns: fromYN_yesno(insured.hasLossRuns),
      insuredIssuedWithInLastSixtyDays: fromYN_yesno(insured.insuredIssuedWithInLastSixtyDays, "na"),
      insuredName: insured.legalName ?? "",
      dba: insured.dbaName ?? "",
      mailingAddress,
      garagingAddress,
      insuredContact: {
        firstName: insured.insuranceContactFirstName ?? "",
        lastName: insured.insuranceContactLastName ?? "",
        email: insured.insuranceContactEmail ?? insured.email ?? "",
        phone: cleanPhone(insured.insuranceContactPhone ?? ""),
      },
      fein: insured.fein ?? "",
      revenue: insured.revenue ?? undefined,
      miles: insured.miles ?? undefined,
      owner: {
        ...(insured.owner ?? {}),
        ...(insured.ownerName
          ? {
              firstName: insured.owner?.firstName ?? insured.ownerName?.split(" ")?.[0] ?? "",
              lastName: insured.owner?.lastName ?? insured.ownerName?.split(" ")?.slice(1).join(" ") ?? "",
            }
          : {}),
        dob: insured.owner?.dob ? new Date(insured.owner.dob) : null,
      },
    },

    // STEP 3
    step3: {
      priorCancelled: fromYN_bool(ops.priorInsuranceCancelledNonrenewed),
      cancellations12mo: fromYN_bool(ops.cancellations12mo),
      uiiaIntermodal: fromYN_bool(ops.UIIAintermodal),
      roadsideConstruction: fromYN_bool(ops.roadsideConstruction),
      dumpLoadsTarped: fromYN_bool(ops.dumpLoadsFullyTarped),
      filingsAlFederal: fromYN_bool(ops.filingsAlFederal),
      filingsAlState: fromYN_bool(ops.filingsAlState),
      freeBoardSixInches: fromYN_bool(ops.driversMaintainSixInchesFreeBoard),
      operatingStart: ops.operatingStart,
      entityType: insured.entityType === "unselected" ? undefined : insured.entityType,
      business_description: ops.businessDescription,
      hasIneligibleOps: fromYN_bool(ops.ineligibleOperations),
      noneOfMentioned: fromYN_bool(ops.noneOfMentioned),
      operationDescription: ops.operationDescription,
      oversizedShipments: fromYN_bool(ops.oversizedShipments),
      brokerageAuthority: fromYN_bool(ops.brokerageAuthority),
      ownerOperators: fromYN_bool(ops.ownerOperators),
      interlineAgreement: fromYN_bool(ops.interlineAgreement),
      interchangeAgreement: fromYN_bool(ops.interchangeAgreement),
      intermodalAgreement: fromYN_bool(ops.intermodalAgreement),
      leaseVehiclesTrailers: fromYN_bool(ops.leaseVehiclesTrailers),
      travelMexico: fromYN_bool(ops.travelMexico),
      travelCanada: fromYN_bool(ops.travelCanada),
      teamDrivers: fromYN_bool(ops.teamDrivers),
      guestPassengers: fromYN_bool(ops.guestPassengers),
      subsidiary: fromYN_bool(ops.subsidiary),
      tripLeasingUnderAuthority: fromYN_bool(ops.tripLeasingUnderAuthority),
    },

    // STEP 4
    step4: {
      carrier: carrierNormalized,
      telemetryOption: ops.dataSharingOption === "unselected" ? undefined : ops.dataSharingOption,
      telemetryProvider: ops.eldVendorName ?? undefined,
    },

    // STEP 5
    step5: {
      carrier: carrierNormalized,
      wayOfCompensation: step5Raw.wayOfCompensation ?? DEFAULTS.step5.wayOfCompensation,
      companyRulesProcedures: fromYN_bool(step5Raw.companyRulesProcedures),
      dailyVehicleInspections: fromYN_bool(step5Raw.dailyVehicleInspections),
      equipmentFamiliarization: fromYN_bool(step5Raw.equipmentFamiliarization),
      routeFamiliarization: fromYN_bool(step5Raw.routeFamiliarization),
      emergencyProcedures: fromYN_bool(step5Raw.emergencyProcedures),
      accidentReportingProcedures: fromYN_bool(step5Raw.accidentReportingProcedures),
      writtenDriverSafetyManual: fromYN_bool(step5Raw.writtenDriverSafetyManual),
      mvrsCheckedAnnually: fromYN_bool(step5Raw.mvrsCheckedAnnually),
      driverHomeDaysPerWeek: step5Raw.driverHomeDaysPerWeek ?? DEFAULTS.step5.driverHomeDaysPerWeek,
      driversDotCompliant: fromYN_bool(step5Raw.driversDotCompliant, true),
      minimumAgeOfDrivers: step5Raw.minimumAgeOfDrivers ?? DEFAULTS.step5.minimumAgeOfDrivers,
      violationsPastSixMonths: step5Raw.violationsPastSixMonths ?? DEFAULTS.step5.violationsPastSixMonths,
      zeroTolerancePolicyForMajorViolations: fromYN_bool(step5Raw.zeroTolerancePolicyForMajorViolations),
      disciplinaryActionForDrivers: step5Raw.disciplinaryActionForDrivers ?? "",
      howOftenAreMandatorySafetyMeetingsHeld: step5Raw.howOftenAreMandatorySafetyMeetingsHeld ?? "",
      maintainAccidentRegister: fromYN_bool(step5Raw.maintainAccidentRegister),
      writtenMaintenanceProgram: fromYN_bool(step5Raw.writtenMaintenanceProgram),
      insuredDoTheirOwnMaintenance: fromYN_bool(step5Raw.insuredDoTheirOwnMaintenance),
      safetyRewardsProgram: fromYN_bool(step5Raw.safetyRewardsProgram),
      cdlExperience: step5Raw.cdlExperience ?? (hasAccelerant ? { year: "", month: "" } : DEFAULTS.step5.cdlExperience),

      drivers: drivers.map(mapDriver),
    },

    // STEP 6
    step6: {
      carrier: carrierNormalized,
      personalUse: fromYN_bool(ops.personalUseOfVehicle, false),
      vehicleFilesDotCompliant: fromYN_bool(
        ops.vehicleFilesDotCompliant,
        true
      ),
      vehicles: vehicles.map(mapVehicle),
    },

    // STEP 7
    step7: {
      trailers: trailers.map(mapTrailer),
      carrier: carrierNormalized,
      nbrOfTrailers: limits.nbrOfTrailers ?? trailers.length ?? 0,
      valueOfTrailers: limits.valueOfTrailers ?? (trailers.length ? sumTrailerValues(trailers) : 0),
    },

    // STEP 8
    step8: {
      carrier: carrierNormalized,

      auto_liability: fromYN_bool(cov.requestAl),
      auto_liability_limit_for_cover_whale_and_nirvana:
        limits.limitAutoLiability && limits.limitAutoLiability !== "0" ? limits.limitAutoLiability : null,
      auto_liability_limit_for_progressive: limits?.autoLiabilityLimitForProgressive,

      personal_injury_protection: fromYN_bool(cov.optAlPip),
      uninsured_motorists_property_damage: fromYN_bool(cov.optAlUm),

      trailer_interchange: fromYN_bool(limits.trailerInterchange),
      trailer_interchange_limit: limits.trailerInterchangeLimit ?? null,

      motor_truck_cargo: fromYN_bool(cov.requestMtc),
      motor_truck_cargo_limit: limits.mtcLimit && limits.mtcLimit !== "0" ? limits.mtcLimit : null,
      mtc_deductible: cov.mtc_deductible ?? null,

      automobile_physical_damage: fromYN_bool(cov.requestApd),
      truckers_general_liability: fromYN_bool(cov.requestTgl),
      non_trucking_liability: fromYN_bool(cov.requestNtl),

      limits: {
        waiverSubrogation: fromYN_bool(limits.insuredWaiverSubrogation),
        primaryNoncontribTGL: fromYN_bool(limits.primaryNoncontributoryTGL),
        primaryNoncontribAL: fromYN_bool(limits.primaryNoncontributoryAL),
        towingLimit: limits.limitTowingStorage ?? null,
      },

      commodity_array: (quote.commodities ?? []).map((c) => ({
        commodityKey: c.commodityKey,
        commodityPercentage: Number(c.commodityPercentage),
        refrigerated: c.refrigerated === "Y" ? true : false,
        generalMerchDetail: c.generalMerchDetail,
      })),
      stateMileage: stateMileage.map((s) => ({
        state: s.state,
        percentage: s.percentage,
        miles: s.miles,
      })),
      radius0_50: radius.radius0_50 ?? undefined,
      radius51_200: radius.radius51_200 ?? undefined,
      radius201_500: radius.radius201_500 ?? undefined,
      radius501: radius.radius501 ?? undefined,
      stateOperation: radius.stateOperation && radius.stateOperation !== "unselected" ? radius.stateOperation : DEFAULTS.step8.stateOperation,
      annualMileage: radius.annualMileage && radius.annualMileage !== "unselected" ? Number(radius.annualMileage) : DEFAULTS.step8.annualMileage,

      // Y/N in DB → enums/booleans in schema
      currentlyInsured: fromYN_yesno(cov.currentlyInsured),
      continuousCoverage: fromYN_yesno(cov.continuousCoverage),

      compCollisionDeductibleChecked: fromYN_bool(cov.compCollisionDeductibleChecked, DEFAULTS.step8.compCollisionDeductibleChecked),
      compCollisionDeductibleOption: cov.compCollisionDeductibleOption ?? DEFAULTS.step8.compCollisionDeductibleOption,

      currentBILimitChecked: fromYN_bool(cov.currentBILimitChecked, DEFAULTS.step8.currentBILimitChecked),
      currentBILimitValue: cov.currentBILimitValue ?? DEFAULTS.step8.currentBILimitValue,

      rentalReimbursement: fromYN_yesOnly(cov.rentalReimbursement),
      roadsideAssistance: fromYN_yesOnly(cov.roadsideAssistance),
      blanketAdditionalInsured: fromYN_yesOnly(cov.blanketAdditionalInsured),

      hiredAutoLiability: fromYN_bool(cov.hiredAutoLiability, DEFAULTS.step8.hiredAutoLiability),
      hiredAutoCount: cov.hiredAutoCount ?? DEFAULTS.step8.hiredAutoCount,

      nonOwnedTrailersCount: cov.nonOwnedTrailersCount ?? DEFAULTS.step8.nonOwnedTrailersCount,
      nonOwnedTrailerCoverage: fromYN_yesOnly(cov.nonOwnedTrailerCoverage),
      nonOwnedTrailers: cov.nonOwnedTrailers ?? DEFAULTS.step8.nonOwnedTrailers,
    },

    // STEP 9
    step9: {
      losses: (lossesArray).map(mapLoss),
    },
    step10: {
      documents,
    },
  };
}
